import { apiFetch } from "@/integrations/backend/client";
import { ApiRequestError } from "@/lib/api-request-error";
import type { ClientProgram, CreateProgramInput } from "@/integrations/backend/programs";

export type SessionStructure = {
  id: string;
  profile_id: string;
  name: string;
  sort_order: number;
  structure: string;
  disabled: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type ProgramPhase = {
  id: string;
  profile_id: string;
  session_structure_id: string | null;
  session_structure_name: string;
  week_start: number;
  week_end: number;
  phase1_start_date: string | null;
  title: string;
  details: string;
  disabled: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type GptLogInputSnapshotRow = { label: string; value: string };

export type GenerationDebugTraceEntry = {
  at: string;
  message: string;
};

export type AIGenerationChatMessage = {
  id: string;
  profile_id: string;
  chat_type:
    | "session_structure"
    | "program_phases"
    | "phase_workouts"
    | "phase_workouts_curated"
    | "program_creation"
    | "assigned_workout_edit";
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string | null;
  updated_at: string | null;
  gpt_log_ids?: string[];
  gpt_log_input_snapshots?: Record<string, GptLogInputSnapshotRow[]>;
};

export type ProgramCreationPreviewPayload = CreateProgramInput;

export type ProgramPhasePreviewPayload = {
  phase1_start_date: string;
  phases: Array<{
    week_start: number;
    week_end: number;
    title: string;
    details: string;
    session_structure_name?: string | null;
  }>;
};

export type SessionStructuresPreviewPayload = {
  session_structures: Array<{
    name: string;
    structure: string;
    sort_order?: number;
  }>;
};

export type PhaseWorkoutPreviewPayload = {
  workouts: Array<{
    name: string;
    date: string;
    description: string;
    duration_minutes: number;
    groupings: Array<{
      sets: number;
      rest_seconds: number;
        exercises: Array<{
        exercise_id: string;
        exercise_name?: string | null;
        sets?: number | null;
        reps?: number | null;
        time_seconds?: number | null;
        rest_seconds?: number | null;
        weight_kg?: number | null;
        weight_lbs?: number | null;
        difficulty?: "Beginner" | "Intermediate" | "Expert" | "Pro" | null;
      }>;
    }>;
  }>;
};

export type AssignedWorkoutEditPreviewPayload = {
  name: string;
  date: string;
  description: string;
  duration_minutes: number;
  groupings: Array<{
    sets: number;
    rest_seconds: number;
    exercises: Array<{
      exercise_id: string;
      exercise_name?: string | null;
      sets?: number | null;
      reps?: number | null;
      time_seconds?: number | null;
      rest_seconds?: number | null;
      weight_kg?: number | null;
      weight_lbs?: number | null;
      difficulty?: "Beginner" | "Intermediate" | "Expert" | "Pro" | null;
    }>;
  }>;
};

export type AIGenerationConversation = {
  conversation_id: string | null;
  chat_type:
    | "session_structure"
    | "program_phases"
    | "phase_workouts"
    | "phase_workouts_curated"
    | "program_creation"
    | "assigned_workout_edit";
  messages: AIGenerationChatMessage[];
  parsed_preview?:
    | ProgramPhasePreviewPayload
    | PhaseWorkoutPreviewPayload
    | ProgramCreationPreviewPayload
    | AssignedWorkoutEditPreviewPayload
    | SessionStructuresPreviewPayload
    | null;
  parse_error?: string | null;
  /** Present when viewer has debugger enabled; ordered backend activity from the latest AI job. */
  generation_debug_trace?: GenerationDebugTraceEntry[];
};

type JsonBody = Record<string, unknown>;

async function requestJson<T>(url: string, method: string, body?: JsonBody): Promise<T> {
  const res = await apiFetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const raw = await res.text();
    let detail = `${method.toLowerCase()}_failed:${res.status}`;
    try {
      const parsed = JSON.parse(raw) as { detail?: unknown };
      if (typeof parsed?.detail === "string") detail = parsed.detail;
      else if (parsed?.detail != null) detail = JSON.stringify(parsed.detail);
    } catch {
      if (raw) detail = raw;
    }
    throw new ApiRequestError(detail, res.status, raw);
  }
  return (await res.json()) as T;
}

export async function listSessionStructures(profileId: string): Promise<SessionStructure[]> {
  const res = await apiFetch(`/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structures`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`session_structures_list_failed:${res.status}`);
  return (await res.json()) as SessionStructure[];
}

export async function createSessionStructure(
  profileId: string,
  input: { structure: string; name?: string; sort_order?: number; disabled?: boolean }
): Promise<SessionStructure> {
  return requestJson<SessionStructure>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structures/create`,
    "POST",
    input
  );
}

export async function updateSessionStructure(
  profileId: string,
  sessionStructureId: string,
  patch: { structure?: string; name?: string; sort_order?: number; disabled?: boolean }
): Promise<SessionStructure> {
  return requestJson<SessionStructure>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structures/${encodeURIComponent(sessionStructureId)}`,
    "PATCH",
    patch
  );
}

export async function deleteSessionStructure(profileId: string, sessionStructureId: string): Promise<void> {
  await requestJson<{ detail: string }>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structures/${encodeURIComponent(sessionStructureId)}`,
    "DELETE"
  );
}

export async function generateSessionStructure(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structure/generate`,
    "POST"
  );
}

export async function getSessionStructureChat(
  profileId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structure/chat${query}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`session_structure_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function sendSessionStructureMessage(
  profileId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structure/send`,
    "POST",
    input
  );
}

export async function restartSessionStructure(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structure/restart`,
    "POST"
  );
}

export async function confirmSessionStructure(
  profileId: string,
  conversationId: string
): Promise<SessionStructure[]> {
  return requestJson<SessionStructure[]>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/session-structure/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

export async function generateProgramCreation(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/create-with-ai/generate`,
    "POST"
  );
}

export async function getProgramCreationChat(
  profileId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/create-with-ai/chat${query}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`program_creation_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function sendProgramCreationMessage(
  profileId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/create-with-ai/send`,
    "POST",
    input
  );
}

export async function restartProgramCreation(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/create-with-ai/restart`,
    "POST"
  );
}

export async function confirmProgramCreation(
  profileId: string,
  conversationId: string
): Promise<ClientProgram> {
  return requestJson<ClientProgram>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/create-with-ai/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

export async function listProgramPhases(profileId: string): Promise<ProgramPhase[]> {
  const res = await apiFetch(`/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`program_phases_list_failed:${res.status}`);
  return (await res.json()) as ProgramPhase[];
}

export async function createProgramPhase(
  profileId: string,
  input: {
    week_start: number;
    week_end: number;
    phase1_start_date: string;
    title: string;
    details: string;
    session_structure_id?: string | null;
    disabled?: boolean;
  }
): Promise<ProgramPhase> {
  return requestJson<ProgramPhase>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/create`,
    "POST",
    input
  );
}

export async function updateProgramPhase(
  profileId: string,
  phaseId: string,
  patch: Partial<{
    week_start: number;
    week_end: number;
    phase1_start_date: string;
    title: string;
    details: string;
    session_structure_id: string | null;
    disabled: boolean;
  }>
): Promise<ProgramPhase> {
  return requestJson<ProgramPhase>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}`,
    "PATCH",
    patch
  );
}

export async function deleteProgramPhase(profileId: string, phaseId: string): Promise<void> {
  await requestJson<{ detail: string }>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}`,
    "DELETE"
  );
}

export async function generateProgramPhases(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/generate`,
    "POST"
  );
}

export async function getProgramPhasesChat(
  profileId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(`/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/chat${query}`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`program_phases_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function sendProgramPhasesMessage(
  profileId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/send`,
    "POST",
    input
  );
}

export async function restartProgramPhases(profileId: string): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/restart`,
    "POST"
  );
}

export async function confirmProgramPhases(profileId: string, conversationId: string): Promise<ProgramPhase[]> {
  return requestJson<ProgramPhase[]>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

export async function generatePhaseWorkouts(
  profileId: string,
  phaseId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}/workouts/generate`,
    "POST"
  );
}

export async function sendPhaseWorkoutsMessage(
  profileId: string,
  phaseId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}/workouts/send`,
    "POST",
    input
  );
}

export async function restartPhaseWorkouts(
  profileId: string,
  phaseId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}/workouts/restart`,
    "POST"
  );
}

export async function getPhaseWorkoutsChat(
  profileId: string,
  phaseId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}/workouts/chat${query}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`phase_workouts_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function confirmPhaseWorkouts(
  profileId: string,
  phaseId: string,
  conversationId: string
): Promise<{ created_workout_ids: string[] }> {
  return requestJson<{ created_workout_ids: string[] }>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/program/phases/${encodeURIComponent(phaseId)}/workouts/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

export async function generateAssignedWorkoutEdit(
  profileId: string,
  assignedWorkoutId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/workouts/assigned/${encodeURIComponent(assignedWorkoutId)}/edit-with-ai/generate`,
    "POST"
  );
}

export async function getAssignedWorkoutEditChat(
  profileId: string,
  assignedWorkoutId: string,
  conversationId?: string | null
): Promise<AIGenerationConversation> {
  const query = conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : "";
  const res = await apiFetch(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/workouts/assigned/${encodeURIComponent(assignedWorkoutId)}/edit-with-ai/chat${query}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error(`assigned_workout_edit_chat_get_failed:${res.status}`);
  return (await res.json()) as AIGenerationConversation;
}

export async function sendAssignedWorkoutEditMessage(
  profileId: string,
  assignedWorkoutId: string,
  input: { conversation_id: string; content: string }
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/workouts/assigned/${encodeURIComponent(assignedWorkoutId)}/edit-with-ai/send`,
    "POST",
    input
  );
}

export async function restartAssignedWorkoutEdit(
  profileId: string,
  assignedWorkoutId: string
): Promise<AIGenerationConversation> {
  return requestJson<AIGenerationConversation>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/workouts/assigned/${encodeURIComponent(assignedWorkoutId)}/edit-with-ai/restart`,
    "POST"
  );
}

export async function confirmAssignedWorkoutEdit(
  profileId: string,
  assignedWorkoutId: string,
  conversationId: string
): Promise<AssignedWorkoutEditPreviewPayload> {
  return requestJson<AssignedWorkoutEditPreviewPayload>(
    `/api/v1/admin/profiles/${encodeURIComponent(profileId)}/workouts/assigned/${encodeURIComponent(assignedWorkoutId)}/edit-with-ai/confirm`,
    "POST",
    { conversation_id: conversationId }
  );
}

function fixJsonUnescapedNewlines(text: string): string {
  let result = "";
  let inString = false;
  let escapeNext = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (escapeNext) {
      result += c;
      escapeNext = false;
      continue;
    }
    if (c === "\\" && inString) {
      result += c;
      escapeNext = true;
      continue;
    }
    if (c === '"') {
      inString = !inString;
      result += c;
      continue;
    }
    if (inString && c === "\n") {
      result += "\\n";
      continue;
    }
    result += c;
  }
  return result;
}

export function parseSessionStructuresAssistantContent(
  content: string
): SessionStructuresPreviewPayload | null {
  const text = (content || "").trim();
  if (!text) return null;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  const candidate = first >= 0 && last > first ? text.slice(first, last + 1) : text;
  for (const raw of [candidate, fixJsonUnescapedNewlines(candidate)]) {
    try {
      const parsed = JSON.parse(raw) as Partial<SessionStructuresPreviewPayload>;
      if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.session_structures)) continue;
      const items = parsed.session_structures
        .filter(
          (item): item is { name: string; structure: string; sort_order?: number } =>
            !!item &&
            typeof item === "object" &&
            typeof (item as { name?: unknown }).name === "string" &&
            typeof (item as { structure?: unknown }).structure === "string"
        )
        .map((item, idx) => ({
          name: item.name.trim(),
          structure: item.structure.trim(),
          sort_order:
            typeof item.sort_order === "number" && item.sort_order > 0 ? item.sort_order : idx + 1,
        }))
        .filter((item) => item.name && item.structure);
      if (items.length === 0) continue;
      return { session_structures: items };
    } catch {
      continue;
    }
  }
  return null;
}

export function parseProgramPhasesAssistantContent(content: string): ProgramPhasePreviewPayload | null {
  const text = (content || "").trim();
  if (!text) return null;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  const candidate = first >= 0 && last > first ? text.slice(first, last + 1) : text;
  for (const raw of [candidate, fixJsonUnescapedNewlines(candidate)]) {
    try {
      const parsed = JSON.parse(raw) as ProgramPhasePreviewPayload;
      if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.phases)) continue;
      return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

export function parseProgramCreationAssistantContent(content: string): ProgramCreationPreviewPayload | null {
  const text = (content || "").trim();
  if (!text) return null;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  const candidate = first >= 0 && last > first ? text.slice(first, last + 1) : text;
  for (const raw of [candidate, fixJsonUnescapedNewlines(candidate)]) {
    try {
      const parsed = JSON.parse(raw) as Partial<ProgramCreationPreviewPayload>;
      if (!parsed || typeof parsed !== "object") continue;
      if (typeof parsed.name !== "string" || typeof parsed.description !== "string") continue;
      if (typeof parsed.duration !== "number" || typeof parsed.frequency !== "number") continue;
      if (!Array.isArray(parsed.motivations) || !Array.isArray(parsed.exercise_types)) continue;
      return {
        name: parsed.name,
        description: parsed.description,
        duration: parsed.duration,
        frequency: parsed.frequency,
        weekly_schedule_preference:
          typeof parsed.weekly_schedule_preference === "string" ? parsed.weekly_schedule_preference : "",
        difficulty: typeof parsed.difficulty === "string" ? parsed.difficulty : "Beginner",
        motivations: parsed.motivations.filter((value): value is string => typeof value === "string"),
        exercise_types: parsed.exercise_types.filter((value): value is string => typeof value === "string"),
        exercise_type_details:
          parsed.exercise_type_details && typeof parsed.exercise_type_details === "object"
            ? Object.fromEntries(
                Object.entries(parsed.exercise_type_details).filter(
                  (entry): entry is [string, string] => typeof entry[0] === "string" && typeof entry[1] === "string"
                )
              )
            : {},
        program_focus: typeof parsed.program_focus === "string" ? parsed.program_focus : "",
        what_to_expect: typeof parsed.what_to_expect === "string" ? parsed.what_to_expect : "",
        variety: typeof parsed.variety === "string" ? parsed.variety : "",
      };
    } catch {
      continue;
    }
  }
  return null;
}

export function parsePhaseWorkoutsAssistantContent(content: string): PhaseWorkoutPreviewPayload | null {
  const text = (content || "").trim();
  if (!text) return null;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  const candidate = first >= 0 && last > first ? text.slice(first, last + 1) : text;
  for (const raw of [candidate, fixJsonUnescapedNewlines(candidate)]) {
    try {
      const parsed = JSON.parse(raw) as PhaseWorkoutPreviewPayload;
      if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.workouts)) continue;
      return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

export function parseAssignedWorkoutEditAssistantContent(
  content: string
): AssignedWorkoutEditPreviewPayload | null {
  const text = (content || "").trim();
  if (!text) return null;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  const candidate = first >= 0 && last > first ? text.slice(first, last + 1) : text;
  for (const raw of [candidate, fixJsonUnescapedNewlines(candidate)]) {
    try {
      const parsed = JSON.parse(raw) as AssignedWorkoutEditPreviewPayload;
      if (!parsed || typeof parsed !== "object") continue;
      if (typeof parsed.name !== "string" || typeof parsed.date !== "string") continue;
      if (typeof parsed.duration_minutes !== "number" || !Array.isArray(parsed.groupings)) continue;
      return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

